import React, { useMemo } from "react";
import { Stack } from "@bookingcom/bui-react";
import useExtraProductsInfoController from "./useExtraProductsInfo.controller.js";
import { InconsistentCabinBaggageInfo } from "../../../../../../../ancillaries/components/ExtraProducts/components/InconsistentCabinBaggageInfo.js";
import { BaggageItem } from "../../../../../../../ancillaries/components/ExtraProducts/components/BaggageItem.js";
import FlexTicketItem from "../../../../../../../ancillaries/components/ExtraProducts/components/FlexTicketItem.js";
import InsuranceItem from "../../../../../../../ancillaries/components/ExtraProducts/components/InsuranceItem.js";
import useProfiler from "../../../../../../../hooks/useProfiler.js";
import ExtraProductsInfoMobile from "./ExtraProductsInfo.mobile.js";
import { track } from "@flights/et-universal";
import useGeniusRewardsInfo from "../GeniusRewardsInfo/useGeniusRewardsInfo.js";
import ExtraProductsInfoDesktop from "./ExtraProductsInfo.desktop.js";
import { getExtraProductsRenderFlags } from "./getExtraProductsRenderFlags.js";
const useExtraProductsInfo = (flight, baggagesExtrasOnly) => {
    const data = useExtraProductsInfoController(flight);
    const { header, subheader, hasData, ancillaries, showLoadingPrice, inconsistentCabinBaggageInfo, cabinBaggagePerTraveller, checkedInBaggage, flexTicket, insurance } = data;
    const { isMobile, isDesktop } = useProfiler();
    const geniusRewardsInfo = useGeniusRewardsInfo(flight, true);
    const geniusFlexTicket = useMemo(() => {
        if (!geniusRewardsInfo?.content)
            return undefined;
        if (!Array.isArray(geniusRewardsInfo.content))
            return undefined;
        if (geniusRewardsInfo.content.length === 0)
            return undefined;
        return geniusRewardsInfo.content?.[0];
    }, [geniusRewardsInfo?.content]);
    if ((!hasData || !ancillaries) && !geniusFlexTicket)
        return undefined;
    const renderFlags = getExtraProductsRenderFlags({
        baggagesExtrasOnly,
        flexTicket,
        insurance,
        geniusFlexTicket,
        cabinBaggagePerTraveller,
        checkedInBaggage,
        inconsistentCabinBaggageInfo
    });
    const isExperimentOn = (isDesktop() && !!track("flights_funnel_new_fd_page_desktop")) ||
        (isMobile() && !!track("flights_funnel_new_fd_page_mobile"));
    if (isExperimentOn) {
        if (!renderFlags.canRenderFlexibility && !renderFlags.canRenderBaggages)
            return undefined;
    }
    else if (!renderFlags.legacyHasContent) {
        return undefined;
    }
    return {
        header,
        subheader,
        hasGeniusFlexTicketOnly: !!geniusFlexTicket && (!hasData || !ancillaries),
        content: isDesktop() && !!track("flights_funnel_new_fd_page_desktop") ? (ExtraProductsInfoDesktop({ ...data, geniusFlexTicket, baggagesExtrasOnly })) : isMobile() && !!track("flights_funnel_new_fd_page_mobile") ? (ExtraProductsInfoMobile({ ...data, geniusFlexTicket, baggagesExtrasOnly })) : (React.createElement(Stack, { gap: 2 },
            inconsistentCabinBaggageInfo && (React.createElement(InconsistentCabinBaggageInfo, { includedProducts: inconsistentCabinBaggageInfo })),
            cabinBaggagePerTraveller && (React.createElement(BaggageItem, { type: "cabinBaggagePerTraveller", product: cabinBaggagePerTraveller, showLoadingPrice: showLoadingPrice, dataTestId: "extra_baggage_cabin" })),
            checkedInBaggage && (React.createElement(BaggageItem, { type: "checkedInBaggage", product: checkedInBaggage, showLoadingPrice: showLoadingPrice, dataTestId: "extra_baggage_checkedin" })),
            flexTicket && (React.createElement("span", null,
                React.createElement(FlexTicketItem, { flexibleTicket: flexTicket, showLoadingPrice: showLoadingPrice }))),
            insurance && React.createElement(InsuranceItem, { travelInsurance: insurance, showLoadingPrice: showLoadingPrice })))
    };
};
export default useExtraProductsInfo;
